import { EstimatorAvatar, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import classnames from 'classnames'
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import PersonIcon from '@material-ui/icons/PersonOutline'
import React from 'react'
import { getActivityColor } from '@paintscout/util'
import { activatable } from './activatable'
import { LocationIcon, PhoneIcon } from './headerIcons'
import type { QuoteDocumentHeaderSection } from './QuoteDocumentHeaderMobileSheet'
import type { QuoteDocumentHeaderData } from './useQuoteDocumentHeaderData'

export interface QuoteDocumentHeaderMobileCustomerProps {
  data: QuoteDocumentHeaderData
  /** Open the read-only detail sheet for a row (Prepared for, Secondary, Job Address, Prepared by). */
  onOpenSection: (section: QuoteDocumentHeaderSection) => void
}

/**
 * Sizes and colours are the prototype's (ENG-2075). Its title colour #354962 has no token (it sits
 * between P40 and P35), so it is hardcoded, as PaymentsTableNext does.
 */
const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        marginBottom: 20
      },
      // The logo + company name above the card. The 28px gap is the prototype's 20px frame padding plus
      // the card's own 8px top padding.
      brandRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minHeight: 36,
        marginBottom: 28
      },
      // Sized by the logo itself, capped so a wide logo still leaves room for the company name.
      logoBox: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        marginRight: -2
      },
      logo: {
        maxWidth: 160,
        maxHeight: 72,
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        display: 'block'
      },
      brandName: {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 500,
        color: '#354962',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      card: {
        border: `0.8px solid ${neutral.P90}`,
        borderRadius: 10,
        backgroundColor: neutral.White,
        overflow: 'hidden'
      },
      summaryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        height: 44,
        padding: theme.spacing(1, 2),
        boxSizing: 'border-box',
        backgroundColor: '#fcfcfd'
      },
      summaryLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1
      },
      summaryLabel: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 500,
        color: neutral.P50,
        whiteSpace: 'nowrap'
      },
      summaryMuted: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 400,
        color: neutral.P60,
        whiteSpace: 'nowrap'
      },
      companyBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        borderTop: `0.8px solid ${neutral.P90}`
      },
      companyName: {
        fontSize: theme.typography.pxToRem(15),
        fontWeight: 500,
        color: '#354962'
      },
      companyMeta: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1)
      },
      metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0
      },
      metaIcon: {
        fontSize: 14,
        width: 14,
        textAlign: 'center',
        color: neutral.P50,
        flexShrink: 0
      },
      metaText: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      commercialBlock: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2),
        borderTop: `0.8px solid ${neutral.P90}`,
        minWidth: 0,
        WebkitTapHighlightColor: 'transparent'
      },
      commercialBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: 1,
        minWidth: 0
      },
      commercialHeading: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0
      },
      commercialCaption: {
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P60
      },
      commercialName: {
        fontSize: theme.typography.pxToRem(15),
        fontWeight: 500,
        color: '#354962',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      row: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        height: 52,
        padding: theme.spacing(2),
        boxSizing: 'border-box',
        borderTop: `0.8px solid ${neutral.P90}`,
        minWidth: 0,
        WebkitTapHighlightColor: 'transparent'
      },
      rowClickable: {
        cursor: 'pointer',
        '&:active': {
          backgroundColor: '#f9fafc'
        }
      },
      rowIcon: {
        fontSize: 16,
        color: neutral.P50,
        flexShrink: 0
      },
      rowLabel: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 400,
        color: neutral.P50,
        whiteSpace: 'nowrap',
        flexShrink: 0
      },
      rowValue: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 600,
        color: neutral.P50,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      chevron: {
        width: 9,
        height: 17,
        flexShrink: 0,
        marginLeft: 'auto',
        color: neutral.P60
      }
    }
  },
  { name: 'QuoteDocumentHeaderMobileCustomer' }
)

/** The prototype's 9×17 chevron-right, the affordance on the rows that open a sheet (ENG-2075). */
function Chevron({ className }: { className?: string }) {
  return (
    <svg className={className} width="9" height="17" viewBox="0 0 9 17" fill="none" aria-hidden="true">
      <path
        d="M6.81693 8.59656C7.06102 8.8197 7.06102 9.18208 6.81693 9.40522L3.06766 12.8326C2.82357 13.0558 2.42716 13.0558 2.18307 12.8326C1.93898 12.6095 1.93898 12.2471 2.18307 12.024L5.49102 9L2.18502 5.97601C1.94093 5.75287 1.94093 5.39049 2.18502 5.16735C2.42912 4.94422 2.82552 4.94422 3.06962 5.16735L6.81888 8.59478L6.81693 8.59656Z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * The phone document header as the customer sees it — the customer view and the estimator's preview
 * dialog (ENG-2075). The editor keeps {@link QuoteDocumentHeaderMobile}.
 *
 * Differs from the editor's phone header in what a customer needs, following the prototype:
 *   - the company logo and name sit above the card, since the mobile nav bar drops its logo here
 *   - the company name, address and phone are always shown under the summary row, not in a sheet
 *   - the summary row is plain text
 *   - Prepared for, Secondary, Job Address and Prepared by open their sheets, and carry a chevron to
 *     say so. The sheet is read-only for a customer: its values stay tappable (tel: / mailto: / maps),
 *     but there is no Edit button (ENG-2075 QA)
 *   - a commercial contact leads with the company, with the person's name under it
 * A row with no value is dropped — there is no "Add …" prompt for a customer.
 */
export default function QuoteDocumentHeaderMobileCustomer({
  data,
  onOpenSection
}: QuoteDocumentHeaderMobileCustomerProps) {
  const classes = useStyles()
  const {
    companyName,
    logo,
    companyAddress,
    companyPhone,
    docLabel,
    quoteId,
    collapsedDate,
    contactName,
    contactCompany,
    secondaryContactName,
    secondaryContactCompany,
    secondaryContactEmails,
    hasSecondaryContact,
    jobIdentifier,
    jobAddressLines,
    showJobAddress,
    owner,
    ownerUser,
    userPictures,
    estimatorName
  } = data

  const companyAddressLine = companyAddress.filter(Boolean).join(', ')
  const jobAddressValue = showJobAddress ? jobIdentifier || jobAddressLines[0] || null : null

  // One 52px row: icon (or avatar), muted label, bold value, and — when it opens a sheet — a chevron.
  const renderRow = (args: {
    icon: React.ReactNode
    label: string
    value?: string | null
    onActivate?: () => void
  }) => {
    const { icon, label, value, onActivate } = args
    if (!value) return null
    return (
      <div
        className={classnames(classes.row, { [classes.rowClickable]: !!onActivate })}
        {...activatable(!!onActivate, () => onActivate())}
      >
        {icon}
        <Typography component="span" className={classes.rowLabel}>
          {label}
        </Typography>
        <Typography component="span" className={classes.rowValue}>
          {value}
        </Typography>
        {onActivate && <Chevron className={classes.chevron} />}
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {(logo || companyName) && (
        <div className={classes.brandRow}>
          {logo && (
            <div className={classes.logoBox}>
              <img className={classes.logo} src={logo} alt={companyName ? `${companyName} logo` : 'Company logo'} />
            </div>
          )}
          {companyName && (
            <Typography component="div" className={classes.brandName}>
              {companyName}
            </Typography>
          )}
        </div>
      )}

      <div className={classes.card}>
        <div className={classes.summaryRow}>
          <div className={classes.summaryLeft}>
            <Typography component="span" className={classes.summaryLabel}>
              {docLabel}
            </Typography>
            {quoteId && (
              <Typography component="span" className={classes.summaryMuted}>
                ID {quoteId}
              </Typography>
            )}
          </div>
          {collapsedDate && (
            <Typography component="span" className={classes.summaryMuted}>
              {collapsedDate}
            </Typography>
          )}
        </div>

        {(companyName || companyAddressLine || companyPhone) && (
          <div className={classes.companyBlock}>
            {companyName && (
              <Typography component="div" className={classes.companyName}>
                {companyName}
              </Typography>
            )}
            {(companyAddressLine || companyPhone) && (
              <div className={classes.companyMeta}>
                {companyAddressLine && (
                  <div className={classes.metaRow}>
                    <LocationIcon className={classes.metaIcon} />
                    <Typography component="span" className={classes.metaText}>
                      {companyAddressLine}
                    </Typography>
                  </div>
                )}
                {companyPhone && (
                  <div className={classes.metaRow}>
                    <PhoneIcon className={classes.metaIcon} />
                    <Typography component="span" className={classes.metaText}>
                      {companyPhone}
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* A commercial contact: the company is who the estimate is for, the person is who to talk to. */}
        {contactCompany ? (
          <div
            className={classnames(classes.commercialBlock, classes.rowClickable)}
            {...activatable(true, () => onOpenSection('contact'))}
          >
            <div className={classes.commercialBody}>
              <div className={classes.commercialHeading}>
                <Typography component="span" className={classes.commercialCaption}>
                  Prepared for
                </Typography>
                <Typography component="span" className={classes.commercialName}>
                  {contactCompany}
                </Typography>
              </div>
              {contactName && (
                <div className={classes.metaRow}>
                  <PersonIcon className={classes.metaIcon} />
                  <Typography component="span" className={classes.metaText}>
                    {contactName}
                  </Typography>
                </div>
              )}
            </div>
            <Chevron className={classes.chevron} />
          </div>
        ) : (
          renderRow({
            icon: <PersonIcon className={classes.rowIcon} />,
            label: 'Prepared for',
            value: contactName,
            onActivate: () => onOpenSection('contact')
          })
        )}

        {hasSecondaryContact &&
          renderRow({
            icon: <PersonIcon className={classes.rowIcon} />,
            label: 'Secondary',
            // A contact saved with no name still has something to show, so fall back to its email.
            value: secondaryContactCompany || secondaryContactName || secondaryContactEmails[0],
            onActivate: () => onOpenSection('secondary-contact')
          })}

        {renderRow({
          icon: <LocationOnIcon className={classes.rowIcon} />,
          label: 'Job Address',
          value: jobAddressValue,
          onActivate: () => onOpenSection('job')
        })}

        {renderRow({
          icon: owner ? (
            <EstimatorAvatar
              user={{ id: owner.id, firstName: owner.firstName, lastName: owner.lastName }}
              userPictures={userPictures}
              width="16px"
              height="16px"
              renderEmpty="blank"
              hideTooltip
              avatarColor={getActivityColor(ownerUser?.user_metadata?.color)}
            />
          ) : (
            <PersonIcon className={classes.rowIcon} />
          ),
          label: 'Prepared by',
          value: estimatorName,
          onActivate: () => onOpenSection('estimator')
        })}
      </div>
    </div>
  )
}
